import { useState, useRef, useEffect } from "react";
import { Radio, X, Minimize2, Maximize2, GripVertical, Signal, AlertTriangle, ChevronLeft, ChevronRight, Wifi } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";

/**
 * DraggableSystemWindow - Floating, draggable window showing a single RF system
 */
export function DraggableSystemWindow({ 
  system, 
  initialPosition, 
  onPositionChange, 
  onClose,
  onMinimize,
  isMinimized,
  spectrumData,
  zIndex,
  onFocus
}) {
  const [position, setPosition] = useState(initialPosition || { x: 100, y: 100 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const windowRef = useRef(null);

  // Calculate signal level from spectrum data
  const getSignalLevel = () => {
    if (!spectrumData || spectrumData.length === 0) return null;
    const systemFreq = parseFloat(system.frequency);
    if (isNaN(systemFreq)) return null;
    
    // Find the closest data point to this frequency
    const closest = spectrumData.reduce((prev, curr) => {
      return Math.abs(curr.frequency - systemFreq) < Math.abs(prev.frequency - systemFreq) ? curr : prev;
    });
    
    if (Math.abs(closest.frequency - systemFreq) <= 1) {
      return closest.level;
    }
    return null;
  };

  const signalLevel = getSignalLevel();
  const hasInterference = signalLevel && signalLevel > -70;
  const hasCriticalInterference = signalLevel && signalLevel > -60;

  // Get status color based on signal level
  const getStatusColor = () => {
    if (hasCriticalInterference) return "#FF3B30"; // Red
    if (hasInterference) return "#FFCC00"; // Yellow
    if (signalLevel !== null) return "#34C759"; // Green
    return "#71717A"; // Gray (no data)
  };

  const handleMouseDown = (e) => {
    if (e.target.closest('button')) return; // Don't drag when clicking buttons
    
    setIsDragging(true);
    onFocus?.();
    const rect = windowRef.current.getBoundingClientRect();
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    
    const newX = e.clientX - dragOffset.x;
    const newY = e.clientY - dragOffset.y;
    
    // Keep within viewport bounds
    const maxX = window.innerWidth - (windowRef.current?.offsetWidth || 200);
    const maxY = window.innerHeight - (windowRef.current?.offsetHeight || 100);
    
    const boundedX = Math.max(0, Math.min(newX, maxX));
    const boundedY = Math.max(0, Math.min(newY, maxY));
    
    setPosition({ x: boundedX, y: boundedY });
  };

  const handleMouseUp = () => {
    if (isDragging) {
      setIsDragging(false);
      onPositionChange?.(system.id, position);
    }
  };

  // Touch support for mobile
  const handleTouchStart = (e) => {
    if (e.target.closest('button')) return;
    
    const touch = e.touches[0];
    setIsDragging(true);
    onFocus?.();
    const rect = windowRef.current.getBoundingClientRect();
    setDragOffset({
      x: touch.clientX - rect.left,
      y: touch.clientY - rect.top
    });
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    
    const touch = e.touches[0];
    const newX = touch.clientX - dragOffset.x;
    const newY = touch.clientY - dragOffset.y;
    
    const maxX = window.innerWidth - (windowRef.current?.offsetWidth || 200);
    const maxY = window.innerHeight - (windowRef.current?.offsetHeight || 100);
    
    setPosition({
      x: Math.max(0, Math.min(newX, maxX)),
      y: Math.max(0, Math.min(newY, maxY))
    });
  };

  const handleTouchEnd = () => {
    if (isDragging) {
      setIsDragging(false);
      onPositionChange?.(system.id, position);
    }
  };

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      window.addEventListener('touchmove', handleTouchMove);
      window.addEventListener('touchend', handleTouchEnd);
      
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
        window.removeEventListener('touchmove', handleTouchMove);
        window.removeEventListener('touchend', handleTouchEnd);
      };
    }
  }, [isDragging, dragOffset]);

  if (isMinimized) {
    return (
      <div
        ref={windowRef}
        className="fixed select-none cursor-move"
        style={{
          left: position.x,
          top: position.y,
          zIndex: zIndex || 100,
        }}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        data-testid={`system-window-minimized-${system.id}`}
      >
        <div 
          className="flex items-center gap-2 px-3 py-2 rounded-full shadow-lg border"
          style={{
            backgroundColor: `${getStatusColor()}20`,
            borderColor: `${getStatusColor()}50`,
          }}
        >
          <div 
            className="w-3 h-3 rounded-full animate-pulse"
            style={{ backgroundColor: getStatusColor() }}
          />
          <span className="text-xs font-mono text-white">{system.channel}</span>
          <span className="text-xs text-[#71717A]">{parseFloat(system.frequency).toFixed(3)}</span>
          <Button
            onClick={() => onMinimize?.(system.id, false)}
            variant="ghost"
            size="sm"
            className="h-5 w-5 p-0 text-[#71717A] hover:text-white"
          >
            <Maximize2 className="w-3 h-3" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={windowRef}
      className={`fixed select-none shadow-xl rounded-sm overflow-hidden ${
        isDragging ? 'cursor-grabbing' : 'cursor-grab'
      }`}
      style={{
        left: position.x,
        top: position.y,
        zIndex: zIndex || 100,
        minWidth: '180px',
        maxWidth: '220px',
      }}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
      data-testid={`system-window-${system.id}`}
    >
      {/* Header - draggable area */}
      <div 
        className="flex items-center justify-between px-3 py-2 border-b"
        style={{
          backgroundColor: `${getStatusColor()}30`,
          borderColor: `${getStatusColor()}50`,
        }}
      >
        <div className="flex items-center gap-2">
          <GripVertical className="w-3 h-3 text-[#71717A]" />
          <div 
            className="w-2 h-2 rounded-full"
            style={{ backgroundColor: getStatusColor() }}
          />
          <span className="text-xs font-semibold text-white truncate max-w-[80px]">
            {system.name || `CH ${system.channel}`}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <Button
            onClick={() => onMinimize?.(system.id, true)}
            variant="ghost"
            size="sm"
            className="h-5 w-5 p-0 text-[#71717A] hover:text-white"
          >
            <Minimize2 className="w-3 h-3" />
          </Button>
          <Button
            onClick={() => onClose?.(system.id)}
            variant="ghost"
            size="sm"
            className="h-5 w-5 p-0 text-[#71717A] hover:text-[#FF3B30]"
          >
            <X className="w-3 h-3" />
          </Button>
        </div>
      </div>
      
      {/* Body */}
      <div className="bg-[#0A0A0A] border border-white/[0.08] border-t-0 p-3">
        {/* Frequency */}
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs text-[#71717A]">Frecuencia</span>
          <span className="text-sm font-mono text-[#34C759] font-bold">
            {parseFloat(system.frequency).toFixed(3)} MHz
          </span>
        </div>
        
        {/* Channel/Group */}
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs text-[#71717A]">Canal</span>
          <span className="text-xs text-white">
            {system.group && `G${system.group} / `}CH {system.channel}
          </span>
        </div>
        
        {/* Signal Level */}
        <div className="flex items-center justify-between">
          <span className="text-xs text-[#71717A]">Señal</span>
          {signalLevel !== null ? (
            <div className="flex items-center gap-1">
              {hasCriticalInterference && (
                <AlertTriangle className="w-3 h-3 text-[#FF3B30] animate-pulse" />
              )}
              <span 
                className="text-sm font-mono font-bold"
                style={{ color: getStatusColor() }}
              >
                {signalLevel.toFixed(1)} dBm
              </span>
            </div>
          ) : (
            <span className="text-xs text-[#52525B]">Sin datos</span>
          )}
        </div>
        
        {/* Status indicator */}
        {hasCriticalInterference && (
          <div className="mt-2 p-2 bg-[#FF3B30]/20 border border-[#FF3B30]/50 rounded text-center">
            <span className="text-xs text-[#FF3B30] font-bold">
              ⚠️ INTERFERENCIA
            </span>
          </div>
        )}
        {hasInterference && !hasCriticalInterference && (
          <div className="mt-2 p-2 bg-[#FFCC00]/20 border border-[#FFCC00]/50 rounded text-center">
            <span className="text-xs text-[#FFCC00] font-bold">
              ⚡ Actividad RF
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * FloatingSystemsManager - Manages all floating system windows
 */
export function FloatingSystemsManager({ 
  systems, 
  spectrumData,
  isVisible,
  onToggleVisibility,
  onSwitchToSidebar,
  showSidebarSwitch
}) {
  const [windowStates, setWindowStates] = useState({});
  const [focusedWindow, setFocusedWindow] = useState(null);
  const [hiddenSystems, setHiddenSystems] = useState(new Set());

  // Load positions from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('avx_floating_systems');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setWindowStates(parsed.windowStates || {});
        setHiddenSystems(new Set(parsed.hiddenSystems || []));
      } catch (e) {
        // Invalid data
      }
    }
  }, []);

  // Save to localStorage when states change
  useEffect(() => {
    localStorage.setItem('avx_floating_systems', JSON.stringify({
      windowStates,
      hiddenSystems: Array.from(hiddenSystems)
    }));
  }, [windowStates, hiddenSystems]);

  // Calculate initial positions in a grid pattern
  const getInitialPosition = (index) => {
    const cols = 3;
    const startX = 250; // Offset from left sidebar
    const startY = 150;
    const gapX = 200;
    const gapY = 180;
    
    const col = index % cols;
    const row = Math.floor(index / cols);
    
    return {
      x: startX + (col * gapX),
      y: startY + (row * gapY)
    };
  };

  const handlePositionChange = (systemId, newPosition) => {
    setWindowStates(prev => ({
      ...prev,
      [systemId]: {
        ...prev[systemId],
        position: newPosition
      }
    }));
  };

  const handleMinimize = (systemId, isMinimized) => {
    setWindowStates(prev => ({
      ...prev,
      [systemId]: {
        ...prev[systemId],
        isMinimized
      }
    }));
  };

  const handleClose = (systemId) => {
    setHiddenSystems(prev => new Set([...prev, systemId]));
  };

  const handleShowAll = () => {
    setHiddenSystems(new Set());
  };

  const handleFocus = (systemId) => {
    setFocusedWindow(systemId);
  };

  if (!isVisible) return null;

  const visibleSystems = systems.filter(s => !hiddenSystems.has(s.id));
  const hasHiddenSystems = hiddenSystems.size > 0;

  return (
    <>
      {/* Control bar */}
      <div className="fixed top-20 right-4 z-50 flex items-center gap-2">
        {hasHiddenSystems && (
          <Button
            onClick={handleShowAll}
            size="sm"
            variant="outline"
            className="border-[#34C759]/50 text-[#34C759] hover:bg-[#34C759]/10 text-xs"
          >
            <Radio className="w-3 h-3 mr-1" />
            Mostrar todos ({hiddenSystems.size} ocultos)
          </Button>
        )}
        {showSidebarSwitch && (
          <Button
            onClick={onSwitchToSidebar}
            size="sm"
            variant="outline"
            className="border-[#007AFF]/50 text-[#007AFF] hover:bg-[#007AFF]/10 text-xs"
          >
            <ChevronRight className="w-3 h-3 mr-1" />
            Modo Sidebar
          </Button>
        )}
        <Button
          onClick={onToggleVisibility}
          size="sm"
          variant="outline"
          className="border-white/20 text-[#71717A] hover:bg-white/5 text-xs"
        >
          <X className="w-3 h-3 mr-1" />
          Cerrar ventanas
        </Button>
      </div>

      {/* Floating windows */}
      {visibleSystems.map((system, index) => {
        const state = windowStates[system.id] || {};
        const position = state.position || getInitialPosition(index);
        const isMinimized = state.isMinimized || false;
        const isFocused = focusedWindow === system.id;
        
        return (
          <DraggableSystemWindow
            key={system.id}
            system={system}
            initialPosition={position}
            onPositionChange={handlePositionChange}
            onClose={handleClose}
            onMinimize={handleMinimize}
            isMinimized={isMinimized}
            spectrumData={spectrumData}
            zIndex={isFocused ? 150 : 100}
            onFocus={() => handleFocus(system.id)}
          />
        );
      })}
    </>
  );
}

/**
 * SystemsToggleButton - Button to toggle floating systems visibility
 */
export function SystemsToggleButton({ systemCount, isActive, onClick }) {
  return (
    <Button
      onClick={onClick}
      variant={isActive ? "default" : "outline"}
      size="sm"
      className={isActive 
        ? "bg-[#34C759] hover:bg-[#34C759]/90 text-black" 
        : "border-[#34C759]/50 text-[#34C759] hover:bg-[#34C759]/10"
      }
      data-testid="toggle-floating-systems"
    >
      <Radio className="w-4 h-4 mr-2" />
      {isActive ? "Sistemas visibles" : "Mostrar sistemas"}
      <span className="ml-2 px-1.5 py-0.5 bg-black/20 rounded text-xs">
        {systemCount}
      </span>
    </Button>
  );
}

/**
 * SystemCardCompact - Compact system card for sidebar
 */
function SystemCardCompact({ system, spectrumData }) {
  // Calculate signal level from spectrum data
  const getSignalLevel = () => {
    if (!spectrumData || spectrumData.length === 0) return null;
    const systemFreq = parseFloat(system.frequency);
    if (isNaN(systemFreq)) return null;
    
    const closest = spectrumData.reduce((prev, curr) => {
      return Math.abs(curr.frequency - systemFreq) < Math.abs(prev.frequency - systemFreq) ? curr : prev;
    });
    
    if (Math.abs(closest.frequency - systemFreq) <= 1) {
      return closest.level;
    }
    return null;
  };

  const signalLevel = getSignalLevel();
  const hasInterference = signalLevel && signalLevel > -70;
  const hasCriticalInterference = signalLevel && signalLevel > -60;

  const getStatusColor = () => {
    if (hasCriticalInterference) return "#FF3B30";
    if (hasInterference) return "#FFCC00";
    if (signalLevel !== null) return "#34C759";
    return "#71717A";
  };

  return (
    <div 
      className={`p-3 rounded-sm border transition-all ${
        hasCriticalInterference 
          ? 'bg-[#FF3B30]/10 border-[#FF3B30]/50 animate-pulse' 
          : hasInterference 
            ? 'bg-[#FFCC00]/10 border-[#FFCC00]/30'
            : 'bg-[#0A0A0A] border-white/[0.05] hover:border-white/[0.1]'
      }`}
      data-testid={`system-card-${system.id}`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <div 
            className="w-2 h-2 rounded-full"
            style={{ backgroundColor: getStatusColor() }}
          />
          <span className="text-sm font-medium text-white truncate max-w-[100px]">
            {system.name || `Sistema ${system.channel}`}
          </span>
        </div>
        {hasCriticalInterference && (
          <AlertTriangle className="w-4 h-4 text-[#FF3B30] animate-bounce" />
        )}
      </div>
      
      <div className="grid grid-cols-2 gap-2 text-xs">
        <div>
          <span className="text-[#52525B]">Freq:</span>
          <span className="ml-1 font-mono text-[#34C759]">
            {parseFloat(system.frequency).toFixed(3)}
          </span>
        </div>
        <div>
          <span className="text-[#52525B]">CH:</span>
          <span className="ml-1 text-white">
            {system.group ? `${system.group}-` : ''}{system.channel}
          </span>
        </div>
      </div>
      
      {signalLevel !== null && (
        <div className="mt-2 pt-2 border-t border-white/[0.05] flex items-center justify-between">
          <span className="text-xs text-[#52525B]">Nivel:</span>
          <span 
            className="text-sm font-mono font-bold"
            style={{ color: getStatusColor() }}
          >
            {signalLevel.toFixed(1)} dBm
          </span>
        </div>
      )}
    </div>
  );
}

/**
 * DesktopSystemsSidebar - Fixed sidebar for desktop showing systems
 */
export function DesktopSystemsSidebar({ 
  systems, 
  spectrumData, 
  isCollapsed, 
  onToggleCollapse,
  isScanning,
  onSwitchToFloating
}) {
  const alertSystems = systems.filter(s => {
    if (!spectrumData || spectrumData.length === 0) return false;
    const freq = parseFloat(s.frequency);
    const closest = spectrumData.reduce((prev, curr) => 
      Math.abs(curr.frequency - freq) < Math.abs(prev.frequency - freq) ? curr : prev
    , spectrumData[0]);
    return closest && Math.abs(closest.frequency - freq) <= 1 && closest.level > -70;
  });

  if (isCollapsed) {
    return (
      <div className="fixed right-0 top-1/2 -translate-y-1/2 z-40">
        <Button
          onClick={onToggleCollapse}
          className="rounded-l-lg rounded-r-none bg-[#111111] border border-r-0 border-white/[0.08] hover:bg-[#1A1A1A] h-auto py-4"
          variant="ghost"
        >
          <div className="flex flex-col items-center gap-2">
            <ChevronLeft className="w-4 h-4 text-[#71717A]" />
            <div className="flex flex-col items-center gap-1">
              <Radio className="w-4 h-4 text-[#34C759]" />
              <span className="text-xs text-white font-bold">{systems.length}</span>
            </div>
            {alertSystems.length > 0 && (
              <div className="flex items-center gap-1 text-[#FF3B30]">
                <AlertTriangle className="w-3 h-3 animate-pulse" />
                <span className="text-xs font-bold">{alertSystems.length}</span>
              </div>
            )}
          </div>
        </Button>
      </div>
    );
  }

  return (
    <div 
      className="fixed right-0 top-0 h-full w-72 bg-[#111111] border-l border-white/[0.08] z-40 flex flex-col"
      data-testid="desktop-systems-sidebar"
    >
      {/* Header */}
      <div className="p-4 border-b border-white/[0.08] flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Radio className="w-5 h-5 text-[#34C759]" />
          <div>
            <h3 className="text-sm font-semibold text-white">Sistemas Inalámbricos</h3>
            <p className="text-xs text-[#71717A]">{systems.length} activos</p>
          </div>
        </div>
        <Button
          onClick={onToggleCollapse}
          variant="ghost"
          size="sm"
          className="text-[#71717A] hover:text-white"
        >
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>

      {/* Scanning status */}
      {isScanning && (
        <div className="px-4 py-2 bg-[#FF3B30]/10 border-b border-[#FF3B30]/30 flex items-center gap-2">
          <div className="w-2 h-2 bg-[#FF3B30] rounded-full animate-pulse" />
          <span className="text-xs text-[#FF3B30] font-medium">ESCANEANDO EN VIVO</span>
        </div>
      )}

      {/* Alert summary */}
      {alertSystems.length > 0 && (
        <div className="px-4 py-3 bg-[#FF3B30]/10 border-b border-[#FF3B30]/30">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-[#FF3B30] animate-pulse" />
            <span className="text-sm text-[#FF3B30] font-bold">
              {alertSystems.length} con interferencia
            </span>
          </div>
        </div>
      )}

      {/* Systems list */}
      <ScrollArea className="flex-1 p-4">
        <div className="space-y-3">
          {systems.length === 0 ? (
            <div className="text-center py-8">
              <Radio className="w-8 h-8 text-[#52525B] mx-auto mb-2" />
              <p className="text-sm text-[#71717A]">No hay sistemas creados</p>
              <p className="text-xs text-[#52525B] mt-1">
                Crea sistemas In-Ear para monitorear
              </p>
            </div>
          ) : (
            systems.map(system => (
              <SystemCardCompact 
                key={system.id} 
                system={system} 
                spectrumData={spectrumData}
              />
            ))
          )}
        </div>
      </ScrollArea>

      {/* Footer */}
      <div className="p-4 border-t border-white/[0.08]">
        <div className="flex items-center justify-between text-xs text-[#71717A] mb-3">
          <span>Rango: 480-980 MHz</span>
          {spectrumData && spectrumData.length > 0 && (
            <span className="flex items-center gap-1">
              <Wifi className="w-3 h-3 text-[#34C759]" />
              {spectrumData.length} pts
            </span>
          )}
        </div>
        {onSwitchToFloating && (
          <Button
            onClick={onSwitchToFloating}
            variant="outline"
            size="sm"
            className="w-full border-[#007AFF]/30 text-[#007AFF] hover:bg-[#007AFF]/10 text-xs"
          >
            <GripVertical className="w-3 h-3 mr-1" />
            Modo Ventanas Flotantes
          </Button>
        )}
      </div>
    </div>
  );
}

/**
 * ResponsiveSystemsView - Shows floating windows on mobile, sidebar on desktop
 * User can manually toggle between modes
 */
export function ResponsiveSystemsView({ 
  systems, 
  spectrumData, 
  isVisible, 
  onToggleVisibility,
  isScanning 
}) {
  const [isMobile, setIsMobile] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [forceFloatingMode, setForceFloatingMode] = useState(false);

  // Detect mobile/tablet vs desktop
  useEffect(() => {
    const checkMobile = () => {
      // Consider mobile/tablet if touch device
      const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      setIsMobile(isTouchDevice);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Load states from localStorage
  useEffect(() => {
    const savedCollapsed = localStorage.getItem('avx_sidebar_collapsed');
    const savedFloating = localStorage.getItem('avx_force_floating');
    if (savedCollapsed) {
      setSidebarCollapsed(JSON.parse(savedCollapsed));
    }
    if (savedFloating) {
      setForceFloatingMode(JSON.parse(savedFloating));
    }
  }, []);

  // Save sidebar state
  useEffect(() => {
    localStorage.setItem('avx_sidebar_collapsed', JSON.stringify(sidebarCollapsed));
  }, [sidebarCollapsed]);

  // Save floating mode preference
  useEffect(() => {
    localStorage.setItem('avx_force_floating', JSON.stringify(forceFloatingMode));
  }, [forceFloatingMode]);

  if (!isVisible) return null;

  // Mobile always uses floating, or if user forced floating mode
  const useFloatingMode = isMobile || forceFloatingMode;

  if (useFloatingMode) {
    return (
      <FloatingSystemsManager
        systems={systems}
        spectrumData={spectrumData}
        isVisible={isVisible}
        onToggleVisibility={onToggleVisibility}
        onSwitchToSidebar={() => setForceFloatingMode(false)}
        showSidebarSwitch={!isMobile}
      />
    );
  }

  // Desktop: Show fixed sidebar on right
  return (
    <DesktopSystemsSidebar
      systems={systems}
      spectrumData={spectrumData}
      isCollapsed={sidebarCollapsed}
      onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
      isScanning={isScanning}
      onSwitchToFloating={() => setForceFloatingMode(true)}
    />
  );
}
